// Derive a coarse "app area" label from a pathname. This is the dimension
// you'd use in GA to compare engagement across the major product surfaces.
// Keep the set small and stable — adding a new bucket means existing reports
// suddenly partition differently.

export type AppArea =
  | 'portal'
  | 'admin'
  | 'internal'
  | 'sales'
  | 'accounting'
  | 'dispatching'
  | 'technician'
  | 'logistics'
  | 'design'
  | 'metrics'
  | 'knowledge'
  | 'client-success'
  // "My Atlas" — the cross-app personal area (Inbox + Profile). Before it was
  // an app these pages fell through to 'other'; from the deploy date onward
  // they partition into their own bucket, so 'other' drops accordingly.
  | 'me'
  | 'super_admin'
  | 'auth'
  | 'shared'
  | 'public'
  | 'other'

const AUTH_PATHS = new Set([
  '/sign-in',
  '/post-sign-in',
  '/forgot-password',
  '/reset-password',
  '/create-account',
  '/accept-invitation',
  '/select-organization',
  '/not-authorized'
])

// Flat pathname→area mapping; cyclomatic count is one branch per surface,
// not logic.
// fallow-ignore-next-line complexity
export function getAppArea(pathname: string | null | undefined): AppArea {
  if (!pathname) return 'other'

  // Top-level routes — no orgSlug prefix
  if (pathname === '/privacy-policy') return 'public'
  if (pathname === '/' || pathname.startsWith('/~offline')) return 'public'
  if (pathname.startsWith('/admin')) return 'super_admin'
  if (pathname.startsWith('/client-success')) return 'client-success'
  if (pathname.startsWith('/shared')) return 'shared'

  // Auth routes can be top-level or under an org slug; check the trailing
  // segment against the known set.
  for (const authPath of AUTH_PATHS) {
    if (pathname === authPath || pathname.endsWith(authPath)) return 'auth'
  }

  // /{orgSlug}/{area}/...  — the area is the second path segment.
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length >= 2) {
    const area = segments[1]
    switch (area) {
      case 'portal':
      case 'admin':
      case 'internal':
      case 'sales':
      case 'accounting':
      case 'dispatching':
      case 'technician':
      case 'logistics':
      case 'design':
      case 'metrics':
      case 'knowledge':
      case 'me':
        return area
    }
  }

  return 'other'
}
